import { Input } from '@/components/form/Input';
import { Container } from '@/components/legacy-ui';
import { NegotiatedPriceInput } from '@/sections/settings/subscription/components';
import {
  SUBSCRIPTION_INPUT_CLASS,
  SUBSCRIPTION_TIERS,
} from '@/sections/settings/subscription/constant';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { parseNonNegativeInt } from '@/utils/number.util';

interface ReviewSectionProps {
  localSubscription: SubscriptionConfiguration;
  tierIncludesWebApps: boolean;
  isPraetorianUser: boolean;
  totalMsrp: number;
  subscriptionFloor: number;
  aiCreditAnnualCost: number;
  aiCreditPackLabel: string;
  aiCreditPackCreditsPerMonth: number;
  subscriptionMsrp: number;
  usagePrice: number;
  onEstimatedCostChange: (value: number) => void;
}

export function ReviewSection({
  localSubscription,
  tierIncludesWebApps,
  isPraetorianUser,
  totalMsrp,
  subscriptionFloor,
  aiCreditAnnualCost,
  aiCreditPackLabel,
  aiCreditPackCreditsPerMonth,
  subscriptionMsrp,
  usagePrice,
  onEstimatedCostChange,
}: ReviewSectionProps) {
  const tierDisplayName =
    SUBSCRIPTION_TIERS.find(t => t.id === localSubscription.selectedTier)
      ?.displayName || localSubscription.selectedTier;

  return (
    <>
      <h3 className="text-chariot-text-primary text-lg font-semibold">
        Review Subscription
      </h3>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Left column: Configuration summary */}
        <Container className="bg-chariot-background-primary space-y-2">
          {/* Subscription details */}
          <SummaryRow label="Tier" value={tierDisplayName} />
          <SummaryRow
            label="Scan Frequency"
            value={localSubscription.scanSchedule}
            capitalize
          />
          <SummaryRow
            label="Asset Coverage"
            value={`${localSubscription.numberOfAssets.toLocaleString()} assets`}
          />
          {tierIncludesWebApps &&
            localSubscription.numberOfApplications > 0 && (
              <SummaryRow
                label="Web FQDNs (DAST)"
                value={`${localSubscription.numberOfApplications.toLocaleString()} apps`}
              />
            )}
          {localSubscription.selectedTier === 'ultimate' &&
            (localSubscription.numberOfAEVExercises ?? 0) > 0 && (
              <SummaryRow
                label="AEV Exercises"
                value={`${localSubscription.numberOfAEVExercises}/year`}
              />
            )}
          {localSubscription.startDate && (
            <SummaryRow
              label="Term"
              value={`${localSubscription.startDate} — ${localSubscription.endDate || 'TBD'}`}
            />
          )}

          {/* AI Credits - merged into same card with a subtle separator */}
          <div className="border-chariot-stroke/50 border-t pt-2">
            <SummaryRow
              label="AI Credit Pack"
              value={
                aiCreditPackCreditsPerMonth > 0
                  ? `${aiCreditPackLabel} (${aiCreditPackCreditsPerMonth.toLocaleString()} credits/mo)`
                  : aiCreditPackLabel
              }
            />
            <SummaryRow
              label="AI Credits Cost"
              value={`$${aiCreditAnnualCost.toLocaleString()}/yr`}
            />
          </div>
        </Container>

        {/* Right column: Pricing */}
        {isPraetorianUser ? (
          <div className="space-y-3">
            <Container
              isSelected
              className="bg-chariot-background-primary space-y-2"
            >
              <SummaryRow
                label="Subscription MSRP"
                value={`$${subscriptionMsrp.toLocaleString()}`}
                bold
              />
              {aiCreditAnnualCost > 0 && (
                <SummaryRow
                  label="AI Credits"
                  value={`$${aiCreditAnnualCost.toLocaleString()}`}
                  bold
                />
              )}
              <div className="border-chariot-stroke flex items-center justify-between border-t pt-2">
                <span className="text-chariot-text-primary text-sm font-bold">
                  Total MSRP
                </span>
                <span className="text-chariot-brand-primary text-xl font-bold">
                  ${totalMsrp.toLocaleString()}
                </span>
              </div>
              <div className="text-chariot-text-secondary text-right text-xs">
                Floor: ${subscriptionFloor.toLocaleString()}
              </div>
            </Container>

            <NegotiatedPriceInput
              value={localSubscription.estimatedCost}
              onChange={onEstimatedCostChange}
              msrp={totalMsrp}
              usagePrice={usagePrice}
              floorPrice={subscriptionFloor}
            />
          </div>
        ) : (
          <Container
            isSelected
            className="bg-chariot-background-primary flex items-center justify-between"
          >
            <h2 className="text-chariot-text-primary text-sm font-medium">
              Estimated Annual Price
            </h2>
            <div className="text-chariot-brand-primary text-2xl font-bold">
              <Input
                name="estimatedCost"
                className={cn(SUBSCRIPTION_INPUT_CLASS, 'text-right')}
                value={localSubscription.estimatedCost.toString()}
                onChange={e =>
                  onEstimatedCostChange(parseNonNegativeInt(e.target.value))
                }
                placeholder="Enter price"
              />
            </div>
          </Container>
        )}
      </div>
    </>
  );
}

/** Reusable summary row for consistent label-value pairs */
function SummaryRow({
  label,
  value,
  capitalize,
  bold,
}: {
  label: string;
  value: string;
  capitalize?: boolean;
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-chariot-text-secondary text-sm">{label}</span>
      <span
        className={cn(
          'text-chariot-text-primary text-sm',
          capitalize && 'capitalize',
          bold ? 'font-bold' : 'font-medium'
        )}
      >
        {value}
      </span>
    </div>
  );
}
